import { getWithExpiry, isDevelop, setWithExpiry } from "@/utils/helper";
import type { AxiosResponse } from "axios";
import axios, { type AxiosRequestConfig } from "axios";
import _ from "lodash";
import type { EmptyObj } from "./type";

export type FetcherResponse<T = any> = Pick<
  AxiosResponse<T>,
  "status" | "statusText" | "data"
>;

const fetcher = async <T = any>(
  config: AxiosRequestConfig,
): Promise<FetcherResponse<T | EmptyObj>> => {
  try {
    const { status, statusText, data } = await axios(config);
    return { status, statusText, data };
  } catch (error: any) {
    if (typeof error === "string") {
      return { status: 400, statusText: error, data: {} };
    }
    if (axios.isAxiosError(error) && error.response) {
      const { status, statusText, data } = error.response;
      return {
        status,
        statusText: data.message || statusText,
        data: data || {},
      };
    }
    return error;
  }
};

type FetcherWithLocalStorageConfig = AxiosRequestConfig & {
  LSConfig: {
    key: string;
    expiry?: number;
  };
};

const fetcherWithLocalStorage = async <T = any>(
  config: FetcherWithLocalStorageConfig,
): Promise<FetcherResponse<T | EmptyObj>> => {
  const key = config.LSConfig.key;
  const oldResponse = getWithExpiry(key);
  const expiry = (config.LSConfig.expiry || isDevelop ? 480 : 120) * 60 * 1000;
  if (
    !_.isEmpty(oldResponse) &&
    (oldResponse.status === 200 || oldResponse.status === 201)
  ) {
    return oldResponse;
  } else {
    const response = await fetcher(config);
    if (
      (response.status === 200 || response.status === 201) &&
      !_.isEmpty(response.data)
    ) {
      setWithExpiry(key, response, expiry);
    }

    return response;
  }
};

export { fetcher, fetcherWithLocalStorage };
